import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Divider,
  Grid,
  Slide,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CakeIcon from '@mui/icons-material/Cake';
import WcIcon from '@mui/icons-material/Wc';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  background: 'linear-gradient(135deg, rgba(255, 246, 250, 0.6), rgba(247, 244, 255, 0.6))',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width=\'52\' height=\'26\' viewBox=\'0 0 52 26\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ff69b4\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4v2c-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6zm25.464-1.95l8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z\' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat',
    opacity: 0.7,
    zIndex: 0,
  }
}));

const AuthPaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  padding: 0,
  borderRadius: 16,
  overflow: 'hidden',
  width: '100%',
  maxWidth: 900,
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)',
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
}));

const FormSide = styled(Box)(({ theme }) => ({
  flex: '1',
  padding: theme.spacing(5, 4),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6, 5),
  },
  display: 'flex',
  flexDirection: 'column',
}));

const ImageSide = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flex: '1',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    background: 'linear-gradient(135deg, var(--gradient-start), var(--gradient-end))',
    color: 'white',
    padding: theme.spacing(6),
  },
}));

const ImageSideContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  padding: theme.spacing(3),
  '& svg': {
    fontSize: 48,
    marginBottom: theme.spacing(3),
  }
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 800,
  marginBottom: theme.spacing(1),
  background: 'linear-gradient(45deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const FormButton = styled(Button)(({ theme }) => ({
  borderRadius: '50px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: 'var(--elevation-2)',
  background: 'linear-gradient(45deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
  marginTop: theme.spacing(2),
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(45deg, var(--gradient-start), var(--gradient-end))',
    boxShadow: 'var(--elevation-3)',
    transform: 'translateY(-2px)',
    filter: 'brightness(0.98)',
  },
  transition: 'all 0.3s ease',
}));

const SwitchButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  textTransform: 'none',
  fontSize: '0.9rem',
  '&:hover': {
    background: 'rgba(123,97,255,0.06)',
  },
}));

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    age: '',
    gender: '',
    interestedIn: [],
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [slideIn, setSlideIn] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);

  const handleToggleMode = () => {
    setSlideIn(false);
    setTimeout(() => {
      setIsLogin(!isLogin);
      setError('');
      setSlideIn(true);
    }, 300);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    
    setFormData(prev => {
      const currentInterests = [...prev.interestedIn];
      
      if (checked && !currentInterests.includes(value)) {
        currentInterests.push(value);
      } else if (!checked && currentInterests.includes(value)) {
        const index = currentInterests.indexOf(value);
        currentInterests.splice(index, 1);
      }
      
      return {
        ...prev,
        interestedIn: currentInterests
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAuthLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        navigate('/memories');
      } else {
        if (formData.interestedIn.length === 0) {
          setError('Please select at least one gender you are interested in');
          setAuthLoading(false);
          return;
        }
        await register(formData);
        navigate('/memories');
      }
    } catch (err) {
      setError(err);
    } finally {
      setAuthLoading(false);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <AuthPaper elevation={3}>
          <FormSide>
            <Slide direction="right" in={slideIn} mountOnEnter unmountOnExit timeout={300}>
              <Box>
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FavoriteIcon sx={{ color: 'var(--gradient-start)', fontSize: 32 }} />
                  <PageTitle variant="h4">
                    {isLogin ? 'Welcome Back' : 'Join Amour Space'}
                  </PageTitle>
                </Box>
                
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  {isLogin 
                    ? 'Sign in to continue your journey of love and connection' 
                    : 'Create an account to start your journey of love and connection'
                  }
                </Typography>
                
                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}
                
                <form onSubmit={handleSubmit}>
                  {/* Email Field */}
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    margin="normal"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: 'var(--gradient-start)' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  
                  {/* Password Field */}
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: 'var(--gradient-start)' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={toggleShowPassword} edge="end">
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  
                  {/* Registration Fields */}
                  {!isLogin && (
                    <>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="First Name"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            required
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon sx={{ color: 'var(--gradient-start)' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Last Name"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            required
                          />
                        </Grid>
                      </Grid>
                      
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Age"
                            name="age"
                            type="number"
                            value={formData.age}
                            onChange={handleChange}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CakeIcon sx={{ color: 'var(--gradient-start)' }} />
                                </InputAdornment>
                              ),
                              inputProps: { min: 18 }
                            }}
                            required
                            helperText="Must be 18 or older"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth required>
                            <InputLabel>Gender</InputLabel>
                            <Select
                              name="gender"
                              value={formData.gender}
                              onChange={handleChange}
                              startAdornment={
                                <InputAdornment position="start">
                                  <WcIcon sx={{ color: 'var(--gradient-start)' }} />
                                </InputAdornment>
                              }
                            >
                              <MenuItem value="male">Male</MenuItem>
                              <MenuItem value="female">Female</MenuItem>
                              <MenuItem value="other">Other</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                      
                      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                        I am interested in:
                      </Typography>
                      <FormGroup row>
                        <FormControlLabel 
                          control={
                            <Checkbox 
                              checked={formData.interestedIn.includes('male')} 
                              onChange={handleCheckboxChange} 
                              value="male"
                              sx={{
                                color: 'var(--gradient-end)',
                                '&.Mui-checked': {
                                  color: 'var(--gradient-end)',
                                },
                              }}
                            />
                          } 
                          label="Men" 
                        />
                        <FormControlLabel 
                          control={
                            <Checkbox 
                              checked={formData.interestedIn.includes('female')} 
                              onChange={handleCheckboxChange} 
                              value="female"
                              sx={{
                                color: 'var(--gradient-start)',
                                '&.Mui-checked': {
                                  color: 'var(--gradient-start)',
                                },
                              }}
                            />
                          } 
                          label="Women" 
                        />
                        <FormControlLabel 
                          control={
                            <Checkbox 
                              checked={formData.interestedIn.includes('other')} 
                              onChange={handleCheckboxChange}
                              value="other"
                              sx={{
                                color: 'var(--gradient-mid)',
                                '&.Mui-checked': {
                                  color: 'var(--gradient-mid)',
                                },
                              }}
                            />
                          } 
                          label="Other" 
                        />
                      </FormGroup>
                    </>
                  )}
                  
                  <FormButton
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={authLoading}
                  >
                    {authLoading ? (isLogin ? 'Signing In...' : 'Creating Account...') : (isLogin ? 'Sign In' : 'Create Account')}
                  </FormButton>
                </form>
                
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      OR
                    </Typography>
                  </Divider>
                  
                  <SwitchButton onClick={handleToggleMode} variant="text" fullWidth>
                    {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
                  </SwitchButton>
                </Box>
              </Box>
            </Slide>
          </FormSide>
          
          <ImageSide>
            <ImageSideContent>
              <FavoriteIcon sx={{ fontSize: 64, color: 'white' }} />
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                {isLogin ? 'Welcome to Amour Space' : 'Join Amour Space Today'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
                {isLogin 
                  ? "Connect with your perfect match and build meaningful relationships that last."
                  : "Find your perfect match, share beautiful moments, and create lasting memories together."
                }
              </Typography>
              <Box component="img" src="/heart-illustration.svg" alt="Love Illustration" sx={{ 
                maxWidth: '80%', 
                height: 'auto',
                opacity: 0.9,
                filter: 'drop-shadow(0 10px 8px rgba(0,0,0,0.15))',
              }} />
            </ImageSideContent>
          </ImageSide>
        </AuthPaper>
      </Container>
    </PageContainer>
  );
};

export default Login; 